import { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Stack, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEqual } from 'lodash';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import {
  PhysicalExaminationInput,
  type PhysicalExaminationInputRef,
} from '@/components/inputs/physical-examination/PhysicalExaminationInput';
import { type PhysicalExaminationData } from '@/components/inputs/physical-examination/types';
import { createEmptyPhysicalExamination } from '@/components/inputs/physical-examination/physical-examination.utils';

export interface PhysicalExaminationInputModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (data: PhysicalExaminationData) => Promise<void>;
  onSaveSuccess?: (data: PhysicalExaminationData) => Promise<void>;
  data?: PhysicalExaminationData;
  mode: 'add' | 'edit';
}

export const PhysicalExaminationInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  data,
  mode,
}: PhysicalExaminationInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Track modal open state to detect when modal opens
  const prevOpenedRef = useRef(false);
  const prevDataRef = useRef<PhysicalExaminationData | undefined>(data);
  const [inputKey, setInputKey] = useState(0);

  // Form state
  const [formData, setFormData] = useState<PhysicalExaminationData>(
    createEmptyPhysicalExamination()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<PhysicalExaminationData | null>(null);
  const formInputRef = useRef<PhysicalExaminationInputRef>(null);

  // Detect if modal is transitioning from closed to open, or if data changed while open
  const isOpening = opened && !prevOpenedRef.current;
  const dataChanged = data !== prevDataRef.current;
  const shouldReinitialize = isOpening || (opened && dataChanged);

  // Compute the effective initial data for the form
  // This runs synchronously on each render, ensuring correct data is available immediately
  const effectiveInitialData = useMemo(() => {
    if (shouldReinitialize) {
      return data
        ? JSON.parse(JSON.stringify(data))
        : createEmptyPhysicalExamination();
    }
    return formData;
  }, [shouldReinitialize, data, formData]);

  // Update refs and state after render
  useEffect(() => {
    if (shouldReinitialize) {
      const newData = data
        ? JSON.parse(JSON.stringify(data))
        : createEmptyPhysicalExamination();
      setFormData(newData);
      baselineRef.current = JSON.parse(JSON.stringify(newData));
      setInputKey((prev) => prev + 1);
    }

    prevOpenedRef.current = opened;
    prevDataRef.current = data;
  }, [opened, data, shouldReinitialize]);

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      return !isEqual(formData, baselineRef.current);
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormData(cloneDeep(baselineRef.current));
    }
    setInputKey((prev) => prev + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Get current data directly from form ref, bypassing debounce
      const currentData = formInputRef.current?.getData() ?? formData;
      await onSave(currentData);
      if (onSaveSuccess) {
        await onSaveSuccess(currentData);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save physical examination:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save physical examination'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={
        mode === 'add'
          ? t('examination.physicalExamination.addExamination', {
              ns: 'patient-pages',
              defaultValue: 'Add Physical Examination',
            })
          : t('examination.physicalExamination.editExamination', {
              ns: 'patient-pages',
              defaultValue: 'Edit Physical Examination',
            })
      }
      size="xl"
    >
      <Stack gap="md" key={inputKey}>
        {/* Error Alert */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        {/* Physical Examination Form */}
        <PhysicalExaminationInput
          ref={formInputRef}
          initialData={effectiveInitialData}
          onChange={setFormData}
        />

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={
            mode === 'add'
              ? t('examination.physicalExamination.addButton', {
                  ns: 'patient-pages',
                  defaultValue: 'Add',
                })
              : t('examination.physicalExamination.saveButton', {
                  ns: 'patient-pages',
                  defaultValue: 'Save',
                })
          }
        />
      </Stack>
    </Modal>
  );
};
